/**
 * Observability Platform — main page component.
 *
 * Auth: Requires login (withAuth wrapper in Page component).
 *
 * Adding a new category requires updating ALARM_CATEGORIES here
 * AND adding the composite alarm in CDK (ObservabilityStack).
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Container,
  Header,
  Link,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Toggle,
} from '@amzn/awsui-components-react-v3/polaris';
import { UserRoles, isInternalUser, useSession } from '@amzn/et-console-components';
import I18n from '../../setupI18n';
import { AlarmDetailsTable } from './components/AlarmDetailsTable';
import { HistoryBar } from './components/HistoryBar';
import { SlackIcon } from './components/SlackIcon';
import { useAlarmDetails } from './hooks/useAlarmDetails';
import { useCategoryAlarmHistory } from './hooks/useCategoryAlarmHistory';
import { useOverallAlarmHistory } from './hooks/useOverallAlarmHistory';
import { ALARM_CATEGORIES, AlarmCategory, SLACK_BUTTON_STYLES, SLACK_CHANNEL_URL } from './components/constants';
import { AlarmDetail, FetchErrorKind, HealthStatus } from './types';

function StatusLabel({ status }: { status: HealthStatus | null }): JSX.Element {
  switch (status) {
    case 'healthy':
      return <StatusIndicator type="success">{I18n.t('Operational')}</StatusIndicator>;
    case 'disrupted':
      return <StatusIndicator type="error">{I18n.t('Disrupted')}</StatusIndicator>;
    default:
      return <StatusIndicator type="stopped">{I18n.t('Unknown')}</StatusIndicator>;
  }
}

function ErrorAlert({ error }: { error: FetchErrorKind }): JSX.Element {
  if (error === 'auth') {
    return (
      <Alert type="warning" header={I18n.t('Unable to load health data')}>
        {I18n.t('Your session may have expired.')}{' '}
        <Link href="/web/login/auth">{I18n.t('Log in again')}</Link>{' '}
        {I18n.t('to view health status.')}
      </Alert>
    );
  }
  return (
    <Alert type="error" header={I18n.t('Unable to load health data')}>
      {I18n.t('Something went wrong.')} <Link href="/web/login/auth">{I18n.t('Log in')}</Link>{' '}
      {I18n.t('or try again later.')}
    </Alert>
  );
}

export function Observability(): JSX.Element {
  const session = useSession();
  const isInternal = 'user' in session && isInternalUser(session);
  const isSysAdmin =
    'user' in session &&
    [UserRoles.SYS_ADMIN, UserRoles.SYS_ADMIN_READ].includes(session.user.role);
  const [showAlarmDetails, setShowAlarmDetails] = useState(false);
  const alarmDetails = useAlarmDetails(isSysAdmin && showAlarmDetails);
  const detailsByCategory = new Map(
    (alarmDetails.data?.categories ?? []).map(({ category, alarms }) => [category, alarms])
  );
  const {
    data: overallData,
    error: overallError,
    isLoading: overallLoading,
    lastUpdated,
  } = useOverallAlarmHistory();

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          isSysAdmin && (
            <Toggle
              checked={showAlarmDetails}
              onChange={({ detail }) => setShowAlarmDetails(detail.checked)}
            >
              {I18n.t('Show alarm details')}
            </Toggle>
          )
        }
      >
        {I18n.t('Observability Platform')}
      </Header>

      <div>
        <Box variant="h3" margin={{ bottom: 'xs' }}>
          {I18n.t('About This Page')}
        </Box>
        <Box color="text-body-secondary">
          {I18n.t(
            'This page monitors the health status of ATMS platform services over the past 30 days. Each bar represents one day — green means all systems were operational, red indicates a disruption was detected.'
          )}
        </Box>
      </div>

      {lastUpdated && (
        <Box color="text-body-secondary" fontSize="body-s">
          {I18n.t('Last updated: %{time}', { time: lastUpdated.toLocaleTimeString() })}
        </Box>
      )}

      {!overallLoading && overallError && <ErrorAlert error={overallError} />}

      {showAlarmDetails && alarmDetails.error && (
        <Alert type="warning" header={I18n.t('Unable to load alarm details')}>
          {I18n.t('Category health above is unaffected. Alarm details retry automatically.')}
        </Alert>
      )}

      <Container
        header={
          <Header
            variant="h2"
            actions={
              overallLoading ? (
                <StatusIndicator type="loading">{I18n.t('Loading')}</StatusIndicator>
              ) : (
                <StatusLabel status={overallData?.overallStatus ?? null} />
              )
            }
          >
            {I18n.t('Overall Health Status')}
          </Header>
        }
      >
        {!overallLoading && overallData && <HistoryBar history={overallData.history} height="40px" />}
      </Container>

      <Header variant="h2" description={I18n.t('Breakdown of service health into categories.')}>
        {I18n.t('Health per Category')}
      </Header>

      {ALARM_CATEGORIES.map(category => (
        <CategoryHealthSection
          key={category.id}
          category={category}
          alarms={showAlarmDetails ? detailsByCategory.get(category.label) : undefined}
        />
      ))}

      {showAlarmDetails && alarmDetails.data && (
        <Container
          header={
            <Header
              variant="h2"
              description={I18n.t(
                'Alarms firing in this account and region that no category includes. Autoscaling alarms are left out.'
              )}
            >
              {I18n.t('Other firing alarms')}
            </Header>
          }
        >
          <SpaceBetween size="s">
            {alarmDetails.data.otherFiringTruncated && (
              <Box color="text-status-warning">
                {I18n.t('This list may be incomplete: more alarms are firing than the page reads.')}
              </Box>
            )}
            <AlarmDetailsTable alarms={alarmDetails.data.otherFiring} />
          </SpaceBetween>
        </Container>
      )}

      {isInternal && (
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <a
                  href={SLACK_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={SLACK_BUTTON_STYLES}
                >
                  <SlackIcon size={16} />
                  {I18n.t('Subscribe via Slack')}
                </a>
              }
            >
              {I18n.t('Get Proactive Alerts in Slack')}
            </Header>
          }
        >
          <Box color="text-body-secondary">
            {I18n.t(
              'The ATMS bot posts when degradation is detected and when services recover.'
            )}
          </Box>
        </Container>
      )}
    </SpaceBetween>
  );
}

/**
 * Renders a single category health section.
 * Each category makes its own BFF call with its composite alarm name.
 *
 * This is a separate component (not inlined in the map) so each category gets
 * its own hook instance and independent loading/error state — one slow or
 * failed category doesn't block the others from rendering.
 */
function CategoryHealthSection({
  category,
  alarms,
}: {
  category: AlarmCategory;
  alarms?: AlarmDetail[];
}): JSX.Element {
  const { data, error, isLoading } = useCategoryAlarmHistory(category.label);

  return (
    <Container
      header={
        <Header
          variant="h3"
          info={
            <Popover
              header={category.label}
              content={category.description}
              triggerType="custom"
              size="medium"
            >
              <Link variant="info">{I18n.t('Info')}</Link>
            </Popover>
          }
          actions={
            isLoading ? (
              <StatusIndicator type="loading">{I18n.t('Loading')}</StatusIndicator>
            ) : (
              <StatusLabel status={data?.overallStatus ?? null} />
            )
          }
        >
          {category.label}
        </Header>
      }
    >
      {!isLoading && error && !data && (
        <Box color="text-status-error">{I18n.t('Unable to load data for this category.')}</Box>
      )}
      {!isLoading && error && data && (
        <Box color="text-status-error" fontSize="body-s">
          {I18n.t('Refresh failed. Showing the last loaded data.')}
        </Box>
      )}
      {!isLoading && data && <HistoryBar history={data.history} height="24px" />}
      {alarms && <AlarmDetailsTable alarms={alarms} />}
    </Container>
  );
}
